import { useState, useMemo } from "react";
import { useParams, Navigate, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useTenant } from "@/hooks/useTenant";
import { useFirmMetrics } from "@/hooks/useFirmMetrics";
import { useOrg } from "@/hooks/useOrg";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  Users,
  Building,
  DollarSign,
  TrendingUp,
  Briefcase,
  Loader2,
  Edit2,
  Check,
  X,
  ArrowLeft,
  Calendar,
  Mail,
  Phone,
  MapPin,
  PieChart,
  CheckSquare,
  Circle,
  Clock,
} from "lucide-react";
import { InvestmentDetailDrawer } from "@/components/firm/InvestmentDetailDrawer";
import { OnboardingStatusCard } from "@/components/firm/OnboardingStatusCard";
import {
  ResponsiveContainer,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
} from "recharts";
import { chartColors } from "@/lib/theme/chartTheme";

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null) return '-';
  if (value >= 1e9) {
    return `$${(value / 1e9).toFixed(2)}B`;
  }
  if (value >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (value >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return `$${value.toFixed(0)}`;
};

const formatAssetType = (type: string): string => {
  const typeMap: Record<string, string> = {
    'private_fund': 'Private Equity',
    'public_fund': 'Public Markets',
    'private_equity': 'Private Equity',
    'public_equity': 'Public Equity',
    'equity': 'Public Equity',
    'fixed_income': 'Fixed Income',
    'real_estate': 'Real Estate',
    'cash': 'Cash',
    'other': 'Other',
  };
  if (typeMap[type]) return typeMap[type];
  // Fallback: convert snake_case to Title Case
  return type.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

export default function FamilyDetail() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { orgId, isMFO, isLoading: tenantLoading } = useTenant();
  const { firmMetrics } = useFirmMetrics();
  const { isOrgAdmin } = useOrg();

  const [isEditing, setIsEditing] = useState(false);
  const [editedOverview, setEditedOverview] = useState("");
  const [selectedInvestment, setSelectedInvestment] = useState<{
    instrumentId: string;
    positionId: string;
  } | null>(null);

  // Fetch family details
  const { data: family, isLoading: familyLoading } = useQuery({
    queryKey: ['family-detail', familyId],
    queryFn: async () => {
      if (!familyId) return null;

      const { data, error } = await supabase
        .from('families')
        .select('*')
        .eq('id', familyId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!familyId,
  });

  // Fetch family holdings with metrics
  const { data: holdings, isLoading: holdingsLoading } = useQuery({
    queryKey: ['family-holdings', familyId],
    queryFn: async () => {
      if (!familyId) return [];

      const { data, error } = await supabase
        .from('positions')
        .select(`
          id,
          instrument_id,
          instruments (
            id,
            name,
            type,
            issuer,
            metadata
          )
        `)
        .eq('family_id', familyId);

      if (error) throw error;

      // Get latest metrics for each position
      const positionIds = data?.map(p => p.id) || [];
      if (positionIds.length === 0) return [];

      const { data: metrics } = await supabase
        .from('position_metrics')
        .select('*')
        .in('position_id', positionIds)
        .order('period_end', { ascending: false });

      // Get latest metric per position
      const latestMetrics = new Map();
      metrics?.forEach(m => {
        if (!latestMetrics.has(m.position_id)) {
          latestMetrics.set(m.position_id, m);
        }
      });

      return data?.map(position => ({
        ...position,
        metrics: latestMetrics.get(position.id) || null,
      })) || [];
    },
    enabled: !!familyId,
  });

  // Fetch family contacts
  const { data: contacts } = useQuery({
    queryKey: ['family-contacts', familyId],
    queryFn: async () => {
      if (!familyId) return [];

      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('family_id', familyId)
        .order('is_primary', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!familyId,
  });

  // Fetch family interactions (meetings)
  const { data: interactions } = useQuery({
    queryKey: ['family-interactions', familyId],
    queryFn: async () => {
      if (!familyId) return [];

      const { data, error } = await supabase
        .from('meetings')
        .select('*')
        .eq('family_id', familyId)
        .order('date', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data || [];
    },
    enabled: !!familyId,
  });

  // Fetch family actions (outstanding items)
  const { data: actions } = useQuery({
    queryKey: ['family-actions', familyId],
    queryFn: async () => {
      if (!familyId) return [];

      const { data, error } = await supabase
        .from('actions')
        .select('*')
        .eq('family_id', familyId)
        .order('due_date', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!familyId,
  });

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalNav = holdings?.reduce((sum, h) => {
      return sum + (Number(h.metrics?.nav_base) || 0);
    }, 0) || 0;

    const positionCount = holdings?.length || 0;

    const totalCommitment = holdings?.reduce((sum, h) => {
      return sum + (Number(h.metrics?.commitment_base) || 0);
    }, 0) || 0;

    // Calculate weighted YTD return
    let weightedReturn = null;
    if (holdings && holdings.length > 0) {
      let totalWeight = 0;
      let weightedSum = 0;
      holdings.forEach(h => {
        const nav = Number(h.metrics?.nav_base) || 0;
        const ret = Number(h.metrics?.twr_ytd) || 0;
        if (nav > 0 && ret !== 0) {
          weightedSum += nav * ret;
          totalWeight += nav;
        }
      });
      if (totalWeight > 0) {
        weightedReturn = (weightedSum / totalWeight) * 100;
      }
    }

    // Calculate % of firm AUM
    const firmAum = firmMetrics?.total_aum_base || 0;
    const pctOfFirm = firmAum > 0 ? (totalNav / firmAum) * 100 : null;

    return {
      totalNav,
      positionCount,
      totalCommitment,
      weightedReturn,
      pctOfFirm,
    };
  }, [holdings, firmMetrics]);

  // Calculate asset allocation
  const assetAllocation = useMemo(() => {
    if (!holdings || holdings.length === 0) return [];

    const allocation: Record<string, number> = {};

    holdings.forEach(h => {
      const nav = Number(h.metrics?.nav_base) || 0;
      const type = h.instruments?.type || 'other';
      const displayType = formatAssetType(type);
      allocation[displayType] = (allocation[displayType] || 0) + nav;
    });

    // Filter out zero values to prevent stacked labels
    return Object.entries(allocation)
      .filter(([, value]) => value > 0)
      .map(([name, value], i) => ({
        name,
        value,
        color: chartColors[i % chartColors.length],
      }));
  }, [holdings]);

  // Save overview mutation
  const saveOverviewMutation = useMutation({
    mutationFn: async (overview: string) => {
      if (!familyId) throw new Error('No family ID');

      const { error } = await supabase
        .from('families')
        .update({ overview })
        .eq('id', familyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['family-detail', familyId] });
      setIsEditing(false);
      toast({
        title: "Overview updated",
        description: "Family overview has been saved.",
      });
    },
    onError: (error) => {
      toast({
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to save overview",
        variant: "destructive",
      });
    },
  });

  const handleStartEdit = () => {
    setEditedOverview(family?.overview || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    saveOverviewMutation.mutate(editedOverview);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedOverview('');
  };

  const isLoading = tenantLoading || familyLoading;

  // Redirect SFO users
  if (!tenantLoading && !isMFO) {
    return <Navigate to="/dashboard" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!family) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <p className="text-muted-foreground">Family not found</p>
        <Button variant="outline" onClick={() => navigate('/firm/families')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Families
        </Button>
      </div>
    );
  }

  const primaryContact = contacts?.find(c => c.is_primary) || contacts?.[0];

  return (
    <div className="flex-1 space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <Button variant="ghost" size="sm" className="gap-1 -ml-2 mb-1" onClick={() => navigate('/firm/families')}>
            <ArrowLeft className="h-4 w-4" />
            All Families
          </Button>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" />
            {family.name}
          </h1>
          {primaryContact && (
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Mail className="h-4 w-4" />
                {primaryContact.email || 'No email'}
              </div>
              {primaryContact.phone && (
                <div className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {primaryContact.phone}
                </div>
              )}
            </div>
          )}
        </div>
        <Button variant="outline" onClick={() => navigate('/firm/families')}>
          <ArrowLeft className="h-4 w-4" />
          Back to Families
        </Button>
      </div>

      {/* Overview Card (Editable) */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Family Overview</CardTitle>
            <CardDescription>Background and key information</CardDescription>
          </div>
          {!isEditing ? (
            isOrgAdmin && (
              <Button variant="ghost" size="sm" onClick={handleStartEdit}>
                <Edit2 className="h-4 w-4" />
                Edit
              </Button>
            )
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saveOverviewMutation.isPending}>
                <Check className="h-4 w-4" />
                Save
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isEditing ? (
            <Textarea
              value={editedOverview}
              onChange={(e) => setEditedOverview(e.target.value)}
              placeholder="Enter family background, investment preferences, key relationships..."
              className="min-h-[100px]"
            />
          ) : (
            <p className="text-muted-foreground">
              {family.overview || 'No overview available. Click Edit to add family background information.'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Onboarding Status Card (for non-active families) */}
      {family.onboarding_status !== 'active' && (
        <OnboardingStatusCard
          familyId={family.id}
          orgId={orgId || ''}
          familyName={family.name}
          onboardingStatus={family.onboarding_status}
          portalEnabled={family.portal_access_enabled}
        />
      )}

      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total AUM</CardTitle>
            <DollarSign className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(kpis.totalNav)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Commitment: {formatCurrency(kpis.totalCommitment)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Positions</CardTitle>
            <Briefcase className="h-4 w-4 text-teal" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{kpis.positionCount}</div>
            <p className="text-xs text-muted-foreground mt-1">Active investments</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">YTD Return</CardTitle>
            <TrendingUp className="h-4 w-4 text-success" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${
              kpis.weightedReturn !== null
                ? kpis.weightedReturn >= 0 ? 'text-success' : 'text-destructive'
                : ''
            }`}>
              {kpis.weightedReturn !== null
                ? `${kpis.weightedReturn >= 0 ? '+' : ''}${kpis.weightedReturn.toFixed(1)}%`
                : '-'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Weighted avg return</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">% of Firm</CardTitle>
            <PieChart className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {kpis.pctOfFirm !== null ? `${kpis.pctOfFirm.toFixed(1)}%` : '-'}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Of firm AUM</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="holdings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="holdings">Holdings</TabsTrigger>
          <TabsTrigger value="allocation">Asset Allocation</TabsTrigger>
          <TabsTrigger value="actions">Actions</TabsTrigger>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="interactions">Interactions</TabsTrigger>
        </TabsList>

        {/* Holdings Tab */}
        <TabsContent value="holdings">
          <Card>
            <CardHeader>
              <CardTitle>Portfolio Holdings</CardTitle>
              <CardDescription>Current investment positions</CardDescription>
            </CardHeader>
            <CardContent>
              {holdingsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : holdings && holdings.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Investment</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>GP/Manager</TableHead>
                      <TableHead className="text-right">Commitment</TableHead>
                      <TableHead className="text-right">NAV</TableHead>
                      <TableHead className="text-right">Called</TableHead>
                      <TableHead className="text-right">TVPI</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {holdings.map((holding) => {
                      const m = holding.metrics;
                      const tvpi = m?.paid_in_base > 0
                        ? (Number(m?.nav_base) + Number(m?.distributed_base || 0)) / Number(m?.paid_in_base)
                        : null;
                      return (
                        <TableRow
                          key={holding.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setSelectedInvestment({
                            instrumentId: holding.instrument_id,
                            positionId: holding.id,
                          })}
                        >
                          <TableCell className="font-medium">
                            {holding.instruments?.name || 'Unknown'}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {holding.instruments?.type || '-'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {holding.instruments?.issuer || '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(m?.commitment_base)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(m?.nav_base)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(m?.paid_in_base)}
                          </TableCell>
                          <TableCell className="text-right">
                            {tvpi !== null ? `${tvpi.toFixed(2)}x` : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No holdings found for this family
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Asset Allocation Tab */}
        <TabsContent value="allocation">
          <Card>
            <CardHeader>
              <CardTitle>Asset Allocation</CardTitle>
              <CardDescription>Portfolio breakdown by asset class</CardDescription>
            </CardHeader>
            <CardContent>
              {assetAllocation.length > 0 ? (
                <div className="h-[400px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={assetAllocation}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={150}
                        fill={chartColors[0]}
                        dataKey="value"
                      >
                        {assetAllocation.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No allocation data available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Actions Tab */}
        <TabsContent value="actions">
          <Card>
            <CardHeader>
              <CardTitle>Action Items</CardTitle>
              <CardDescription>Outstanding tasks and next steps</CardDescription>
            </CardHeader>
            <CardContent>
              {actions && actions.length > 0 ? (
                <div className="space-y-3">
                  {actions.map((action) => {
                    const isOverdue = action.due_date && new Date(action.due_date) < new Date();
                    const statusIcon = action.status === 'completed' ? (
                      <Check className="h-4 w-4 text-success" />
                    ) : action.status === 'in_progress' ? (
                      <Clock className="h-4 w-4 text-warning" />
                    ) : (
                      <Circle className="h-4 w-4 text-muted-foreground" />
                    );
                    return (
                      <div
                        key={action.id}
                        className={`flex items-start gap-3 p-4 rounded-lg border ${
                          isOverdue && action.status !== 'completed' ? 'border-destructive/50 bg-destructive/5' : ''
                        }`}
                      >
                        <div className="mt-0.5">{statusIcon}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium">{action.title}</div>
                          {action.description && (
                            <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                              {action.description}
                            </p>
                          )}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            {action.due_date && (
                              <span className={isOverdue && action.status !== 'completed' ? 'text-destructive font-medium' : ''}>
                                Due: {new Date(action.due_date).toLocaleDateString()}
                              </span>
                            )}
                            {action.priority && (
                              <Badge variant={action.priority === 'high' ? 'destructive' : action.priority === 'medium' ? 'secondary' : 'outline'} className="text-xs">
                                {action.priority}
                              </Badge>
                            )}
                            <Badge variant="outline" className="text-xs capitalize">
                              {action.status?.replace('_', ' ') || 'open'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No action items for this family
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts">
          <Card>
            <CardHeader>
              <CardTitle>Family Contacts</CardTitle>
              <CardDescription>Key contacts and stakeholders</CardDescription>
            </CardHeader>
            <CardContent>
              {contacts && contacts.length > 0 ? (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="flex items-start justify-between p-4 rounded-lg border"
                    >
                      <div className="space-y-1">
                        <div className="font-medium flex items-center gap-2">
                          {contact.first_name} {contact.last_name}
                          {contact.is_primary && (
                            <Badge variant="secondary" className="text-xs">Primary</Badge>
                          )}
                        </div>
                        {contact.title && (
                          <p className="text-sm text-muted-foreground">{contact.title}</p>
                        )}
                        <div className="flex flex-wrap gap-4 mt-2 text-sm">
                          {contact.email && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="h-3 w-3" />
                              {contact.email}
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Phone className="h-3 w-3" />
                              {contact.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No contacts found for this family
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Interactions Tab */}
        <TabsContent value="interactions">
          <Card>
            <CardHeader>
              <CardTitle>Recent Interactions</CardTitle>
              <CardDescription>Meetings, calls, and notes</CardDescription>
            </CardHeader>
            <CardContent>
              {interactions && interactions.length > 0 ? (
                <div className="space-y-4">
                  {interactions.map((interaction) => (
                    <div
                      key={interaction.id}
                      className="flex items-start gap-4 p-4 rounded-lg border"
                    >
                      <div className="rounded-full p-2 bg-muted">
                        <Calendar className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="font-medium">{interaction.title}</div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(interaction.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </p>
                        {interaction.notes && (
                          <p className="text-sm mt-2">{interaction.notes}</p>
                        )}
                      </div>
                      <Badge variant="outline">{interaction.type || 'Meeting'}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No recent interactions found
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Investment Detail Drawer */}
      <InvestmentDetailDrawer
        open={!!selectedInvestment}
        onOpenChange={(open) => !open && setSelectedInvestment(null)}
        instrumentId={selectedInvestment?.instrumentId || null}
        currentFamilyId={familyId}
        currentFamilyName={family?.name}
        positionId={selectedInvestment?.positionId}
      />
    </div>
  );
}
